import React, { lazy, Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext.jsx";
import { ConfigProvider } from "./contexts/ConfigContext.jsx";
import { trackPageView } from "./utils/analytics";
import ErrorBoundary from "./components/ErrorBoundary";

// Core pages - loaded immediately
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import NotFoundPage from "./pages/NotFoundPage";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import NewsletterPopup from "./components/NewsletterPopup";
import CookieConsent from "./components/CookieConsent";

// Admin protection components
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import SuperAdminProtectedRoute from "./components/SuperAdminProtectedRoute";

// Admin pages - lazy-loaded to keep the public bundle small (code-splitting)
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminMonitoringDashboard = lazy(() => import("./pages/AdminMonitoringDashboard"));
const AdminConfigPage = lazy(() => import("./pages/AdminConfigPage"));

// Lazy-loaded pages for better performance
const CompetitionPage = lazy(() => import("./pages/CompetitionPage"));
const SubmissionPage = lazy(() => import("./pages/SubmissionPage"));
const GalleryPage = lazy(() => import("./pages/FinalistsGalleryPage"));
const WinnersGalleryPage = lazy(() => import("./pages/WinnersGalleryPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ProfileSetupPage = lazy(() => import("./pages/ProfileSetupPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentTestPage = lazy(() => import("./pages/PaymentTestPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const JudgesPage = lazy(() => import("./pages/JudgesPage"));
const AwardsPage = lazy(() => import("./pages/AwardsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const RulesPage = lazy(() => import("./pages/RulesPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const JudgeDashboard = lazy(() => import("./pages/JudgeDashboard"));
const JudgePage = lazy(() => import("./pages/JudgePage"));
const JudgeYNVoting = lazy(() => import("./pages/JudgeYNVoting"));
const JudgeScoringPhase2 = lazy(() => import("./pages/JudgeScoringPhase2"));
const JudgeRanking = lazy(() => import("./pages/JudgeRanking"));
const JudgeFinalResults = lazy(() => import("./pages/JudgeFinalResults"));
const JudgeGalleryPage = lazy(() => import("./pages/JudgeGalleryPage"));
const JudgeScoringPage = lazy(() => import("./pages/JudgeScoringPage"));
const JudgeFinalistsPage = lazy(() => import("./pages/JudgeFinalistsPage"));
const FinalistUploadPage = lazy(() => import("./pages/FinalistUploadPage"));
const TestPage = lazy(() => import("./pages/TestPage"));
const AuthTestPage = lazy(() => import("./pages/AuthTestPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SubmissionSuccessPage = lazy(() => import("./pages/SubmissionSuccessPage"));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
      <p className="text-orange-400 text-lg">Loading...</p>
    </div>
  </div>
);

// Analytics wrapper to track page views
function AnalyticsTracker({ children }) {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location]);

  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <div className="App min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      <AuthProvider>
        <ConfigProvider>
        <BrowserRouter>
          <AnalyticsTracker>
            <Navigation />
            <main className="min-h-screen">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
              {/* EMAIL VERIFICATION ROUTES - Both paths for compatibility */}
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/email-verify" element={<EmailVerificationPage />} />
              
              <Route path="/" element={<HomePage />} />
              <Route path="/competition" element={<Navigate to="/competition/2026" replace />} />
              <Route path="/competition/:year" element={<CompetitionPage />} />
              <Route path="/submit" element={<SubmissionPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/gallery/:subcatSlug" element={<GalleryPage />} />
              <Route path="/winners" element={<WinnersGalleryPage />} />
              <Route path="/winners/:categorySlug" element={<WinnersGalleryPage />} />
              <Route path="/profile/:id" element={<ProfilePage />} />
              <Route path="/profile-setup" element={<ProfileSetupPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/super-admin" element={<SuperAdminDashboard />} />
              <Route path="/system-login" element={<AdminLoginPage />} />
              <Route path="/admin/monitoring" element={
                <AdminProtectedRoute>
                  <AdminMonitoringDashboard />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/config" element={
                <AdminProtectedRoute>
                  <AdminConfigPage />
                </AdminProtectedRoute>
              } />
              <Route path="/test" element={<TestPage />} />
              <Route path="/auth-test" element={<AuthTestPage />} />
              
              {/* JUDGE ROUTES */}
              <Route path="/judge" element={<JudgePage />} />
              <Route path="/judge/vote/:category/:photographer_category" element={<JudgeYNVoting />} />
              <Route path="/judge/score/:category/:photographer_category" element={<JudgeScoringPhase2 />} />
              <Route path="/judge/rank/:category/:photographer_category" element={<JudgeRanking />} />
              <Route path="/judge/results" element={<JudgeFinalResults />} />
              <Route path="/judge/results/:category/:photographer_category" element={<JudgeFinalResults />} />
              <Route path="/judge-dashboard" element={<JudgeDashboard />} />
              <Route path="/judge/gallery/:category/:type" element={<JudgeGalleryPage />} />
              <Route path="/judge/scoring/:category/:type" element={<JudgeScoringPage />} />
              <Route path="/judge/finalists/:category/:type" element={<JudgeFinalistsPage />} />
              
              {/* FINALIST ROUTES */}
              <Route path="/finalist-upload" element={<FinalistUploadPage />} />
              
              {/* INFORMATIONAL PAGES */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/rules" element={<RulesPage />} />
              <Route path="/judges" element={<JudgesPage />} />
              <Route path="/awards" element={<AwardsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/register" element={<RegisterPage />} />
              
              {/* LEGAL PAGES */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/faq" element={<TermsPage />} /> {/* FAQ shares same page as terms */}
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              
              {/* Payment pages */}
              <Route path="/payment-test" element={<PaymentTestPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/submission-success" element={<SubmissionSuccessPage />} />
              
              {/* 404 PAGE - Must be last */}
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <NewsletterPopup />
          <CookieConsent />
          <Toaster />
          </AnalyticsTracker>
        </BrowserRouter>
        </ConfigProvider>
      </AuthProvider>
    </div>
    </ErrorBoundary>
  );
}

export default App;